import axios from 'axios'
import store from '@/store'
import router from '@/router'
import i18n from '@/i18n'
import { logout } from '@/utils/user'

import { showNotice } from '@/utils/tool'

import { setupCache } from 'axios-cache-adapter'
import localforage from 'localforage'
const forageStore = localforage.createInstance({
  // List of drivers used
  driver: [
    localforage.LOCALSTORAGE
  ],
  // Prefix all storage keys to prevent conflicts
  name: 'my-cache'
})

// Create `axios-cache-adapter` instance
const cache = setupCache({
  store: forageStore
})

// ETag 快取：第一次收到資料時把 ETag 與資料存進 IndexedDB（localStorage 只有 5MB 不夠放），
// 之後帶 If-None-Match，後端回 304 就改用 IndexedDB 的資料
// 呼叫端以 customConfig: { etagCache: true } 開啟
// etag 與 data 分開存，送 request 時只讀小的 etag，304 時才讀大的 data
const etagStore = localforage.createInstance({
  driver: [
    localforage.INDEXEDDB
  ],
  name: 'etag-cache'
})
// 一載入就先開好資料庫，第一次讀 ETag 不用再等開啟
etagStore.ready().catch(() => {})
const ETAG_CACHE_LIMIT = 2 // 最多保留幾筆（例如切換語言），超過就淘汰最舊的
const ETAG_RECENT_KEY = '__recent__'
// 本頁面收到的最新 ETag，寫進 IndexedDB 前（或寫入失敗）連同資料留在記憶體，
// 同一頁面接著發的請求不必等寫入完成，也不會帶到舊的 ETag
const etagMemory = new Map()
// 寫入快取前記下、寫完才清掉；下次載入時還在，代表上次寫入失敗或寫到一半就換頁
const ETAG_WRITE_FLAG = 'etag-cache-writing'

function setEtagWriteFlag(writing) {
  try {
    if (writing) localStorage.setItem(ETAG_WRITE_FLAG, '1')
    else localStorage.removeItem(ETAG_WRITE_FLAG)
  } catch (e) {
    // localStorage 不能用就不記
  }
}

function hasEtagWriteFlag() {
  try {
    return !!localStorage.getItem(ETAG_WRITE_FLAG)
  } catch (e) {
    return false
  }
}

function getEtagCacheKey(config, formData) {
  const params = []
  for (const [key, value] of formData.entries()) {
    if (key !== 'token') params.push(`${key}=${value}`)
  }
  params.sort()
  return (config.baseURL || '') + config.url + '?' + params.join('&')
}

// 回傳 ETag 與快取狀態，狀態帶給後端統計沒帶 ETag 的原因：
// ok 有 ETag／noentry 沒有紀錄／write-failed 上次寫入失敗或被中斷／read-error 讀取出錯
async function readEtag(key) {
  const memory = etagMemory.get(key)
  if (memory) return { etag: memory.etag, state: 'ok' }
  try {
    const etag = await etagStore.getItem(key + '|etag')
    if (etag) return { etag, state: 'ok' }
    return { etag: null, state: hasEtagWriteFlag() ? 'write-failed' : 'noentry' }
  } catch (e) {
    // 不支援 IndexedDB（如部分無痕模式）就當作沒有快取
    console.warn('[etag-cache] 讀取 ETag 失敗', e)
    return { etag: null, state: 'read-error' }
  }
}

async function readEtagData(key) {
  try {
    const memory = etagMemory.get(key)
    if (memory && memory.text) return JSON.parse(memory.text)
    const data = await etagStore.getItem(key + '|data')
    // 存的是原始 JSON 字串；舊版存的是物件，直接用
    return typeof data === 'string' ? JSON.parse(data) : data
  } catch (e) {
    console.warn('[etag-cache] 讀取快取資料失敗', e)
    return null
  }
}

function clearEtagCache(key) {
  etagMemory.delete(key)
  etagStore.removeItem(key + '|etag').catch(() => {})
}

// 資料存成一個字串，比存 9 千多筆物件寫得快，也比較不容易在換頁時被中斷
function writeEtagCache(key, etag, text) {
  const entry = { etag, text }
  etagMemory.set(key, entry)
  setEtagWriteFlag(true)
  saveEtagCache(key, etag, text).then(() => {
    setEtagWriteFlag(false)
    // 已寫進 IndexedDB，記憶體只留 ETag，資料要用時再讀（期間又收到更新的就不動）
    if (etagMemory.get(key) === entry) etagMemory.set(key, { etag })
  }, e => {
    // 寫入失敗（QuotaExceededError、交易被中斷等）：清掉 IndexedDB 的 ETag，資料留在記憶體給這個頁面用
    console.warn('[etag-cache] 寫入快取失敗', e)
    etagStore.removeItem(key + '|etag').catch(() => {})
  })
}

async function saveEtagCache(key, etag, text) {
  const recent = [key, ...((await etagStore.getItem(ETAG_RECENT_KEY)) || []).filter(k => k !== key)]
    .slice(0, ETAG_CACHE_LIMIT)

  // 先清掉不在保留名單的資料（含被淘汰的與舊格式留下的），空出空間再寫
  const keep = new Set([ETAG_RECENT_KEY])
  recent.forEach(k => keep.add(k + '|etag').add(k + '|data'))
  const keys = await etagStore.keys()
  for (const k of keys) {
    if (!keep.has(k)) await etagStore.removeItem(k)
  }

  // 先寫 data 再寫 etag，確保有 etag 就一定有 data
  await etagStore.setItem(key + '|data', text)
  await etagStore.setItem(key + '|etag', etag)
  await etagStore.setItem(ETAG_RECENT_KEY, recent)
}

// create an axios instance
const service = axios.create({
  adapter: cache.adapter,
  timeout: 60000 // request timeout
})
// request interceptor
service.interceptors.request.use(
  async config => {
    if (!(config.customConfig && config.customConfig.noLoading)) {
      store.commit('SET_LOADING_COUNT_PLUS')
    }

    const formData = new FormData()
    for (var key in config['data']) {
      if (config['data'][key] !== null && config['data'][key] !== undefined) {
        formData.set(key, config['data'][key])
      }
    }

    if (!formData.has('code')) {
      let brandCode = store.getters.brandCode
      if (!brandCode || brandCode === '') {
        brandCode = process.env.VUE_APP_BRAND_CODE // 開發環境指定brand code
        if (brandCode === '') {
          brandCode = window.location.host || document.location.host // 剛開始載入先用host取資訊
        }
      }

      formData.set('code', brandCode)
    }

    if (!formData.has('token') && store.getters.token) {
      formData.set('token', store.getters.token)
    }

    if (!formData.has('language')) {
      formData.set('language', store.getters.language)
    }

    config['data'] = formData

    if (config.customConfig && config.customConfig.etagCache) {
      const cacheKey = getEtagCacheKey(config, formData)
      const { etag, state } = await readEtag(cacheKey)
      config.etagCacheKey = cacheKey
      config.headers['X-Cache-State'] = state
      if (etag) {
        config.headers['If-None-Match'] = etag
      }
      config.validateStatus = status => (status >= 200 && status < 300) || status === 304
      // 留下原始 JSON 字串，存快取時不用再 stringify 一次
      const raw = config.etagRaw = {}
      config.transformResponse = [data => {
        raw.text = data
        return data
      }, ...service.defaults.transformResponse]
    }

    // store.commit('SET_LOADING', true)
    return config
  },
)

// response interceptor
service.interceptors.response.use(
  async function(response) {
    if (!(response.config.customConfig && response.config.customConfig.noLoading)) {
      store.commit('SET_LOADING_COUNT_MINUS')
    }

    const cacheKey = response.config.etagCacheKey
    if (!cacheKey) return response

    if (response.status === 304) {
      // 版本沒變，用本地資料
      const data = await readEtagData(cacheKey)
      if (data) {
        response.data = data
        return response
      }
      // 有 ETag 卻讀不到資料（被瀏覽器清掉或讀取失敗）：清掉 ETag，不帶 If-None-Match 重打一次
      clearEtagCache(cacheKey)
      const headers = { ...response.config.headers }
      delete headers['If-None-Match']
      headers['X-Cache-State'] = 'noentry'
      response = await axios.request({ ...response.config, headers })
    }

    const etag = response.headers['etag']
    if (etag && response.status !== 304) {
      const raw = response.config.etagRaw.text
      writeEtagCache(cacheKey, etag, typeof raw === 'string' ? raw : JSON.stringify(response.data))
    }

    return response
  },
  error => {
    if (!(error.config.customConfig && error.config.customConfig.noLoading)) {
      store.commit('SET_LOADING_COUNT_MINUS')
    }
    store.commit('SET_LOADING', false)
    const showErrorMsg = !(error.config && error.config.customConfig && error.config.customConfig.mute)

    if (error.response === undefined) {
      // 其他錯誤訊息
      const msg = i18n.te('Bao.please_check_network') ? i18n.t('Bao.please_check_network') : 'Please check your network status.'
      // brand API 尚未成功時（載入中或已失敗）頁面只剩公開正文（見 main.js），其餘請求的網路層失敗不再逐一跳提示蓋住正文（僅 theme_gold）
      // 載入中也要擋：請求比 brand API 先失敗時 brandStatus 仍是 loading
      const brandDown = process.env.VUE_APP_THEME === 'theme_gold' && !store.getters.brandReady
      if (showErrorMsg && !brandDown) {
        showNotice(msg, function() {
          if (router.currentRoute.name === 'error' || router.currentRoute.name === 'critical') {
            window.top.location.href = '/'
          } else {
            window.top.location.reload()
          }
        })
      }
    } else {
      const content = error.response.data
      // console.clear()
      if (content.status === 401) {
        // 重新登入
        logout(content.msg)
      } else if (content.status === 403) {
        // 權限不足 回首頁
        // showNotice(content.msg, function() {
        //   router.push({ path: '/index' })
        // })
      } else if (content.status === 510) {
        // toErrorPage(content.data.content)
      } else if (content.status === 406) {
        // 表單欄位顯示 不由這邊處理
      } else if (content.status === 505) {
        // 不由這邊處理
      } else {
        // 其他錯誤訊息
        if (showErrorMsg) {
          showNotice(content.msg)
        }
      }
    }

    return Promise.reject(error)
  }
)

export default service
